import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "~/components/ui/button";
import { TOAST_MESSAGES } from "~/constants/messages";
import { useTutorialAccessGuard } from "~/hooks/useTutorialAccessGuard";
import ChartIcon from "~/images/chart.svg?react";
import DocumentIcon from "~/images/document.svg?react";
import HistoryIcon from "~/images/history.svg?react";
import TableIcon from "~/images/table.svg?react";
import type { RootState } from "~/store";
import { advanceTutorialStep } from "~/utils/tutorialEvents";
import styles from "./index.module.scss";

type StepStatus = "pending" | "generating" | "completed";

type StepCardProps = {
  icon: React.ReactNode;
  title: string;
  action: string;
  name: string;
  withBorder?: boolean;
  isGeneratable?: boolean;
  onClick?: () => void;
  isGenerating?: boolean;
  isGenerated?: boolean;
  isDisabled?: boolean;
  dataTourId?: string;
  creditUsage?: number;
  canGenerate?: boolean;
  isHighlighted?: boolean;
  statusLabels?: Partial<Record<StepStatus, string>>;
};

const SUMMARY_FLAG = "reqxign_tutorial_summary_generated";
const SYSTEM_FLAG = "reqxign_tutorial_system_generated";
const FLOW_RESET_FLAG = "reqxign_tutorial_flow_reset_done";

const StepCard: React.FC<StepCardProps> = ({
  icon,
  title,
  action,
  withBorder = false,
  isGeneratable = true,
  onClick,
  isGenerating = false,
  isGenerated = false,
  isDisabled = false,
  dataTourId,
  creditUsage,
  canGenerate = true,
  isHighlighted = false,
  statusLabels,
}) => {
  const isButtonDisabled = isGenerating || isDisabled || !canGenerate;

  const defaultStatusLabels: Record<StepStatus, string> = {
    pending: "未生成",
    generating: "生成中",
    completed: "生成済",
  };

  const currentStatus: StepStatus = isGenerating
    ? "generating"
    : isGenerated
      ? "completed"
      : "pending";

  const label = statusLabels?.[currentStatus] ?? defaultStatusLabels[currentStatus];

  const handleContainerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onClick && !isButtonDisabled) {
      onClick();
    }
  };

  return (
    <div
      className={`${styles["card"]} ${withBorder ? styles["withBorder"] : ""}`}
      data-tour-id={dataTourId}
    >
      <div className={styles["cardHeader"]}>
        <span>
          {icon}
          {title}
        </span>
        <div
          className={[
            styles["buttonContainer"],
            isButtonDisabled ? styles["generating"] : "",
            isHighlighted ? styles["highlighted"] : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={handleContainerClick}
        >
          {creditUsage !== undefined && !isButtonDisabled && (
            <div className={styles["creditTooltip"]}>
              {creditUsage === 0 ? "クレジット消費なし" : "クレジット消費なし"}
            </div>
          )}
          {isGeneratable && (
            <Button label={action} styleType="white-small" disabled={isButtonDisabled} />
          )}
        </div>
      </div>
      {label ? (
        <div className={styles["time"]}>
          <HistoryIcon width={14} height={14} />
          {label}
        </div>
      ) : null}
    </div>
  );
};

const TutorialDashboard: React.FC = () => {
  useTutorialAccessGuard();
  const navigate = useNavigate();
  const tourStep = useSelector((state: RootState) => state.auth.tour_step);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [systemProposalGenerated, setSystemProposalGenerated] = useState(false);
  const isProjectTopStep = tourStep === 0;
  const highlightOrder = useMemo(
    () => [
      "summary",
      "system-proposal",
      "estimation",
      "cost-effectiveness",
      "functional-requirements",
      "non-functional-requirements",
      "workflow",
      "plugin-proposal",
      "app-design",
      "field-design",
      "system-architecture",
      "entity-relationship",
      "customize-specification",
    ],
    [],
  );
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const highlightTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const generationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Make the STEP2 (生成フロー) reset run only once per tutorial session
  const didFlowResetRef = useRef(false);

  type CardStatus = "pending" | "generating" | "completed";
  const [cardStatuses, setCardStatuses] = useState<Record<string, CardStatus>>(() => {
    const initial: Record<string, CardStatus> = {};
    [
      "summary",
      "system-proposal",
      "estimation",
      "cost-effectiveness",
      "functional-requirements",
      "non-functional-requirements",
      "workflow",
      "plugin-proposal",
      "app-design",
      "field-design",
      "system-architecture",
      "entity-relationship",
      "customize-specification",
    ].forEach((key) => {
      initial[key] = "pending";
    });
    return initial;
  });

  // Fallback sync: if visual status marks system-proposal as completed,
  // ensure boolean + localStorage are aligned so gating doesn't regress on STEP3.
  const systemProposalVisualStatus = cardStatuses["system-proposal"];
  useEffect(() => {
    if (systemProposalVisualStatus === "completed" && !systemProposalGenerated) {
      setSystemProposalGenerated(true);
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem(SYSTEM_FLAG, "true");
        }
      } catch {
        // ignore
      }
    }
  }, [systemProposalVisualStatus, systemProposalGenerated]);

  // Also sync summary visual status to boolean/localStorage so STEP3 reflects generated state
  const summaryVisualStatus = cardStatuses["summary"];
  useEffect(() => {
    if (summaryVisualStatus === "completed" && !summaryGenerated) {
      setSummaryGenerated(true);
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem(SUMMARY_FLAG, "true");
        }
      } catch {
        // ignore
      }
    }
  }, [summaryVisualStatus, summaryGenerated]);

  // Ensure STEP3 starts with STEP3 documents visually ungenerated (pending)
  // while keeping STEP1/STEP2 persisted states intact.
  useEffect(() => {
    // Reset STEP3 view every time we (re)enter STEP3.
    // - Make STEP1/STEP2 (summary/system-proposal) ungenerated visually and logically
    // - Make STEP3 cards ungenerated visually
    // Use location.key to ensure it runs on same-route re-navigation as well
    if (tourStep === 3) {
      // Visual reset
      setCardStatuses((prev) => ({
        ...prev,
        summary: "pending",
        "system-proposal": "pending",
        "functional-requirements": "pending",
        "non-functional-requirements": "pending",
        workflow: "pending",
      }));

      // Logical reset (boolean + localStorage) for STEP1/STEP2
      setSummaryGenerated(false);
      setSystemProposalGenerated(false);
      try {
        if (typeof window !== "undefined") {
          localStorage.removeItem(SUMMARY_FLAG);
          localStorage.removeItem(SYSTEM_FLAG);
        }
      } catch {
        // ignore
      }
    }
  }, [tourStep]);

  const cardToSection: Record<string, "step1" | "step2" | "step3" | "step4"> = {
    summary: "step1",
    "system-proposal": "step2",
    estimation: "step2",
    "cost-effectiveness": "step2",
    "functional-requirements": "step3",
    "non-functional-requirements": "step3",
    workflow: "step3",
    "plugin-proposal": "step3",
    "app-design": "step4",
    "field-design": "step4",
    "system-architecture": "step4",
    "entity-relationship": "step4",
    "customize-specification": "step4",
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const summaryDone = localStorage.getItem(SUMMARY_FLAG) === "true";
    const systemDone = localStorage.getItem(SYSTEM_FLAG) === "true";
    setSummaryGenerated(summaryDone);
    setSystemProposalGenerated(systemDone);
    setCardStatuses((prev) => {
      const next: Record<string, CardStatus> = {
        ...prev,
        summary: summaryDone ? "completed" : (prev["summary"] as CardStatus),
        "system-proposal": systemDone ? "completed" : (prev["system-proposal"] as CardStatus),
      };
      return next;
    });
  }, []);

  // When entering the "生成フロー" step (dashboard-explanation, tourStep === 2),
  // reset all documents to the ungenerated state for a fresh walkthrough.
  // Guard with localStorage so it only runs once per tutorial session and not on remounts.
  useEffect(() => {
    if (tourStep === 2) {
      const alreadyReset = (() => {
        try {
          return typeof window !== "undefined" && localStorage.getItem(FLOW_RESET_FLAG) === "true";
        } catch {
          return false;
        }
      })();
      if (alreadyReset) return;
      didFlowResetRef.current = true; // best-effort guard within same mount
      setSummaryGenerated(false);
      setSystemProposalGenerated(false);
      setCardStatuses((prev) => {
        const reset: Record<string, CardStatus> = {};
        Object.keys(prev).forEach((k) => (reset[k] = "pending"));
        return reset;
      });
      try {
        if (typeof window !== "undefined") {
          localStorage.removeItem(SUMMARY_FLAG);
          localStorage.removeItem(SYSTEM_FLAG);
          localStorage.setItem(FLOW_RESET_FLAG, "true");
        }
      } catch {
        // ignore storage errors
      }
    }
  }, [tourStep]);

  // In the tutorial step "システム概要書の生成", assume Summary is already generated
  useEffect(() => {
    if (typeof window === "undefined") return;
    // tourStep indexes: ... 6 = generate-system-proposal
    if (tourStep >= 6 && !summaryGenerated) {
      setSummaryGenerated(true);
      setCardStatuses((prev) => ({
        ...prev,
        summary: "completed",
      }));
      try {
        localStorage.setItem(SUMMARY_FLAG, "true");
      } catch {
        // ignore storage failures in environments without localStorage
      }
    }
  }, [tourStep, summaryGenerated]);

  useEffect(() => {
    // Do not reset generated states on tour step changes except the dedicated flow reset (tourStep === 2) handled above.
    // Keeping state ensures System Proposal remains generated when navigating to STEP3.
  }, []);

  useEffect(() => {
    if (highlightTimerRef.current) {
      clearInterval(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }

    if (tourStep === 2 && highlightOrder.length > 0) {
      setHighlightIndex(0);
      highlightTimerRef.current = setInterval(() => {
        setHighlightIndex((prev) => {
          if (prev < highlightOrder.length - 1) {
            return prev + 1;
          }

          // Reset visual highlight states only; keep persisted tutorial flags
          setCardStatuses(() => {
            const resetStatuses: Record<string, CardStatus> = {};
            highlightOrder.forEach((key) => {
              resetStatuses[key] = "pending";
            });
            return resetStatuses;
          });

          return 0;
        });
      }, 1600);
    } else {
      setHighlightIndex(-1);
    }

    return () => {
      if (highlightTimerRef.current) {
        clearInterval(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, [tourStep, highlightOrder]);

  const highlightedCard = highlightIndex >= 0 ? highlightOrder[highlightIndex] : null;
  const highlightedSection = highlightedCard ? (cardToSection[highlightedCard] ?? null) : null;

  const getCardStatus = (card: string): CardStatus => cardStatuses[card] ?? "pending";
  const isCardGenerating = (card: string) => getCardStatus(card) === "generating";
  const isCardCompleted = (card: string) => getCardStatus(card) === "completed";

  useEffect(() => {
    if (generationTimerRef.current) {
      clearTimeout(generationTimerRef.current);
      generationTimerRef.current = null;
    }

    if (!highlightedCard) return;

    // Do not reset persisted states when highlighting STEP4; keep tutorial flow intact

    if (getCardStatus(highlightedCard) === "completed") {
      return;
    }

    setCardStatuses((prev) => ({
      ...prev,
      [highlightedCard]: "generating",
    }));

    generationTimerRef.current = setTimeout(() => {
      setCardStatuses((prev) => ({
        ...prev,
        [highlightedCard]: "completed",
      }));
      // Visual highlight should not flip persisted tutorial flags
    }, 900);

    return () => {
      if (generationTimerRef.current) {
        clearTimeout(generationTimerRef.current);
        generationTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getCardStatus uses cardStatuses state which would cause infinite loop if added
  }, [highlightedCard, getCardStatus]);

  const handleUploadSummary = () => {
    if (summaryGenerated) {
      setCardStatuses((prev) => ({
        ...prev,
        summary: "completed",
      }));
      setTimeout(() => {
        navigate("/tutorial/summary");
      }, 100);
      return;
    }
    toast.success(TOAST_MESSAGES.TUTORIAL.MINUTES_UPLOADED);
    setCardStatuses((prev) => ({
      ...prev,
      summary: "generating",
    }));
    setTimeout(() => {
      setSummaryGenerated(true);
      setCardStatuses((prev) => ({
        ...prev,
        summary: "completed",
      }));
      if (typeof window !== "undefined") {
        localStorage.setItem(SUMMARY_FLAG, "true");
      }
      advanceTutorialStep();
      navigate("/tutorial/summary");
    }, 1500);
  };

  const handleGenerateSystemProposal = () => {
    if (systemProposalGenerated) {
      setCardStatuses((prev) => ({
        ...prev,
        "system-proposal": "completed",
      }));
      navigate("/tutorial/system-proposal");
      return;
    }
    if (!summaryGenerated) {
      toast.warn(TOAST_MESSAGES.TUTORIAL.STEP1_REQUIRED);
      return;
    }
    toast.success(TOAST_MESSAGES.TUTORIAL.SYSTEM_OVERVIEW_SUCCESS);
    setCardStatuses((prev) => ({
      ...prev,
      "system-proposal": "generating",
    }));
    setTimeout(() => {
      setSystemProposalGenerated(true);
      setCardStatuses((prev) => ({
        ...prev,
        "system-proposal": "completed",
      }));
      if (typeof window !== "undefined") {
        localStorage.setItem(SYSTEM_FLAG, "true");
      }
      advanceTutorialStep();
      navigate("/tutorial/system-proposal");
    }, 1500);
  };

  const handleGenerateEstimation = () => {
    if (isCardCompleted("estimation")) {
      setCardStatuses((prev) => ({ ...prev, estimation: "completed" }));
      return;
    }
    if (!systemProposalGenerated) {
      toast.warn(TOAST_MESSAGES.TUTORIAL.STEP2_REQUIRED);
      return;
    }
    // Start estimation generation
    setCardStatuses((prev) => ({ ...prev, estimation: "generating" }));
    setTimeout(() => {
      // Complete estimation
      setCardStatuses((prev) => ({ ...prev, estimation: "completed" }));
      // Then trigger cost-effectiveness generating → completed
      setCardStatuses((prev) => ({ ...prev, "cost-effectiveness": "generating" }));
      setTimeout(() => {
        setCardStatuses((prev) => ({ ...prev, "cost-effectiveness": "completed" }));
      }, 1000);
    }, 1500);
  };

  return (
    <div data-tour-id="dashboard-main">
      <section
        className={[
          styles["stepSection"],
          highlightedSection === "step1" ? styles["sectionHighlight"] : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-tour-id="dashboard-step1"
      >
        <h2 className={styles["step"]}>STEP1</h2>
        <StepCard
          icon={<DocumentIcon width={20} height={20} />}
          title="議事録"
          action={summaryGenerated ? "開く" : "生成"}
          name="summary"
          onClick={handleUploadSummary}
          creditUsage={0}
          canGenerate={true}
          isGenerating={isCardGenerating("summary")}
          isGenerated={summaryGenerated}
          isDisabled={isProjectTopStep}
          dataTourId="dashboard-upload"
          isHighlighted={highlightedCard === "summary"}
        />
      </section>
      <p className={styles["note"]}>議事録をもとにシステム概要書を生成</p>
      <section
        className={[
          styles["stepSection2"],
          highlightedSection === "step2" ? styles["sectionHighlight"] : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-tour-id="dashboard-step2"
      >
        <div className={styles["stepSection"]} data-tour-id="generate-system-proposal">
          <h2>STEP2</h2>
          <StepCard
            icon={<DocumentIcon width={20} height={20} />}
            title="システム概要書"
            action={systemProposalGenerated ? "開く" : "生成"}
            name="system-proposal"
            isGeneratable={tourStep === 2 ? true : summaryGenerated}
            onClick={handleGenerateSystemProposal}
            creditUsage={10}
            canGenerate={summaryGenerated}
            isGenerating={isCardGenerating("system-proposal")}
            isGenerated={systemProposalGenerated}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "system-proposal"}
          />
        </div>

        <div className={styles["cardRow2"]}>
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="概算工数見積もり"
            action={isCardCompleted("estimation") ? "開く" : "生成"}
            name="estimation"
            withBorder
            isGeneratable={systemProposalGenerated}
            creditUsage={5}
            canGenerate={systemProposalGenerated}
            onClick={handleGenerateEstimation}
            isGenerating={isCardGenerating("estimation")}
            isGenerated={isCardCompleted("estimation")}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "estimation"}
          />
          <StepCard
            icon={<ChartIcon width={20} height={20} />}
            title="費用対効果"
            action="開く"
            name="cost-effectiveness"
            withBorder
            isGeneratable={false}
            isGenerating={isCardGenerating("cost-effectiveness")}
            isGenerated={isCardCompleted("cost-effectiveness")}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "cost-effectiveness"}
          />
        </div>
      </section>
      <p className={styles["note"]}>システム概要書をもとに要件定義書を生成</p>
      <section
        className={[
          styles["stepSection"],
          highlightedSection === "step3" ? styles["sectionHighlight"] : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <h2>STEP3</h2>
        <h3 className={styles["stepTitle"]}>要件定義書</h3>
        <div className={styles["cardRow"]}>
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="機能要件書"
            action="生成"
            name="functional-requirements"
            withBorder
            creditUsage={10}
            canGenerate={systemProposalGenerated || isCardCompleted("system-proposal")}
            isGeneratable={systemProposalGenerated || isCardCompleted("system-proposal")}
            isGenerating={isCardGenerating("functional-requirements")}
            isGenerated={isCardCompleted("functional-requirements")}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "functional-requirements"}
          />
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="非機能要件書"
            action="生成"
            name="non-functional-requirements"
            withBorder
            creditUsage={10}
            canGenerate={systemProposalGenerated || isCardCompleted("system-proposal")}
            isGeneratable={systemProposalGenerated || isCardCompleted("system-proposal")}
            isGenerating={isCardGenerating("non-functional-requirements")}
            isGenerated={isCardCompleted("non-functional-requirements")}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "non-functional-requirements"}
          />
          <StepCard
            icon={<ChartIcon width={20} height={20} />}
            title="業務フロー図"
            action="生成"
            name="workflow"
            withBorder
            creditUsage={10}
            canGenerate={systemProposalGenerated || isCardCompleted("system-proposal")}
            isGeneratable={systemProposalGenerated || isCardCompleted("system-proposal")}
            isGenerating={isCardGenerating("workflow")}
            isGenerated={isCardCompleted("workflow")}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "workflow"}
          />
        </div>
        <p className={styles["note"]}>機能要件書をもとにプラグイン提案書を生成</p>
        <StepCard
          icon={<TableIcon width={20} height={20} />}
          title="プラグイン提案書"
          action="生成"
          name="plugin-proposal"
          withBorder
          creditUsage={10}
          canGenerate={isCardCompleted("functional-requirements")}
          isGeneratable={isCardCompleted("functional-requirements")}
          isGenerating={isCardGenerating("plugin-proposal")}
          isGenerated={isCardCompleted("plugin-proposal")}
          isDisabled={isProjectTopStep}
          isHighlighted={highlightedCard === "plugin-proposal"}
        />
      </section>
      <p className={styles["note"]}>要件定義の内容をもとに基本設計書を生成</p>
      <section
        className={[
          styles["stepSection"],
          highlightedSection === "step4" ? styles["sectionHighlight"] : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <h2>STEP4</h2>
        <h3 className={styles["stepTitle"]}>基本設計書</h3>
        <StepCard
          icon={<DocumentIcon width={20} height={20} />}
          title="アプリ設計書"
          action="生成"
          name="app-design"
          withBorder
          creditUsage={10}
          canGenerate={isCardCompleted("functional-requirements")}
          isGeneratable={isCardCompleted("functional-requirements")}
          isGenerating={isCardGenerating("app-design")}
          isGenerated={isCardCompleted("app-design")}
          isDisabled={isProjectTopStep}
          isHighlighted={highlightedCard === "app-design"}
        />
        <p className={styles["note"]}>アプリ設計書をもとにシステム構成図,フィールド設計書を生成</p>
        <div className={styles["cardRow"]}>
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="フィールド設計書"
            action="生成"
            name="field-design"
            withBorder
            creditUsage={10}
            canGenerate={isCardCompleted("app-design")}
            isGeneratable={isCardCompleted("app-design")}
            isGenerating={isCardGenerating("field-design")}
            isGenerated={isCardCompleted("field-design")}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "field-design"}
          />
          <StepCard
            icon={<ChartIcon width={20} height={20} />}
            title="システム構成図"
            action="生成"
            name="system-architecture"
            withBorder
            creditUsage={10}
            canGenerate={
              (systemProposalGenerated || isCardCompleted("system-proposal")) &&
              isCardCompleted("app-design")
            }
            isGeneratable={
              (systemProposalGenerated || isCardCompleted("system-proposal")) &&
              isCardCompleted("app-design")
            }
            isGenerating={isCardGenerating("system-architecture")}
            isGenerated={isCardCompleted("system-architecture")}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "system-architecture"}
          />
        </div>
        <p className={styles["note"]}>フィールド設計書をもとにER図,カスタマイズ仕様書を生成</p>
        <div className={styles["cardRow"]}>
          <StepCard
            icon={<ChartIcon width={20} height={20} />}
            title="ER図"
            action="生成"
            name="entity-relationship"
            withBorder
            creditUsage={10}
            canGenerate={isCardCompleted("field-design")}
            isGeneratable={isCardCompleted("field-design")}
            isGenerating={isCardGenerating("entity-relationship")}
            isGenerated={isCardCompleted("entity-relationship")}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "entity-relationship"}
          />
          <StepCard
            icon={<TableIcon width={20} height={20} />}
            title="カスタマイズ仕様書"
            action="生成"
            name="customize-specification"
            withBorder
            creditUsage={10}
            canGenerate={isCardCompleted("field-design") && isCardCompleted("app-design")}
            isGeneratable={isCardCompleted("field-design") && isCardCompleted("app-design")}
            isGenerating={isCardGenerating("customize-specification")}
            isGenerated={isCardCompleted("customize-specification")}
            isDisabled={isProjectTopStep}
            isHighlighted={highlightedCard === "customize-specification"}
          />
        </div>
      </section>
    </div>
  );
};

export default TutorialDashboard;
